import { Observable, timer } from 'rxjs';

import { ExtensionApiFactory, IdleState, IExtensionApiStrategy } from '~cdw-ae/common/frontend/browser-extension-api';

import { setActive, setInactive } from './user-activity.state';

import { environment } from '../../../environments/environment';
import { RootState, rootStore } from '../extension-store/extension.store';
import { StateStatus } from '../extension-store/interfaces/extension-store.interfaces';
import { OFFLINE_SYNC_FIELDS, OfflineSyncFields } from '../offlineSync/offline-sync.interfaces';
import { addHydratedSessionPing, addOfflineSessionPing, addSessionPing } from '../update-session/update-session.state';
import UtilsService from '../utils/utils.service';

let extensionApi: IExtensionApiStrategy;
const userActivitySessionTrigger$: Observable<number> = timer(
  10 * 1000,
  environment.defaultTimings.userSessionCreateRateInSeconds * 1000
);

const checkAndSendOfflinePings = async (appState: RootState): Promise<void> => {
  extensionApi = ExtensionApiFactory();
  const offlineFields = await extensionApi.getLocalStorage<OfflineSyncFields>(OFFLINE_SYNC_FIELDS);
  if (!Array.isArray(offlineFields?.offlineSessionPings) || offlineFields.offlineSessionPings.length === 0) {
    return;
  }

  for (const ping of offlineFields.offlineSessionPings) {
    const hydratedSessionPayload = {
      deviceId: ping.sessionPayload.deviceId ?? appState.deviceDetails.details.id,
      userId: ping.sessionPayload.userId ?? appState.userProfile.userProfile.email,
      customerId: ping.sessionPayload.customerId ?? appState.customerState.customerSettings?.customerId ?? 'unknown',
      domain: ping.sessionPayload.domain ?? appState.userProfile.userProfile.domain,
      deviceOrgUnitPath:
        ping.sessionPayload.deviceOrgUnitPath ?? appState.deviceDetails.deviceResource?.deviceOrgUnitPath ?? 'unknown',
      userOrgUnitPath:
        ping.sessionPayload.userOrgUnitPath ?? appState.userProfile.userProfile.userOrgUnitPath ?? 'unknown',
      publicIp: ping.sessionPayload.publicIp ?? appState.networkStatus.networkStatus.publicIp,
      privateIp: ping.sessionPayload.privateIp ?? appState.networkStatus.networkStatus.privateIp,
      crosVersion: ping.sessionPayload.crosVersion ?? appState.deviceDetails.details.OSVersion ?? 'unknown',
      extensionVersion: ping.sessionPayload.extensionVersion ?? extensionApi.getManifest().version,
      timestamp: ping.sessionPayload.timestamp ?? new Date().toISOString(),
    };
    rootStore.dispatch(addHydratedSessionPing({ id: ping.id, sessionPayload: hydratedSessionPayload }));
  }
  await extensionApi.setLocalStorage({ offlineSessionPings: [] });
};

const UserActivityService = {
  init: () => {
    UtilsService.debugLog('User Activity Service Init');
    extensionApi = ExtensionApiFactory();
    extensionApi.setIdleStateDetectionInterval(environment.defaultTimings.idleDetectionIntervalInSeconds);
    extensionApi.addIdleStateChangeListener((idleState: IdleState) => {
      if (idleState === 'active') {
        rootStore.dispatch(setActive());
        return;
      }
      rootStore.dispatch(setInactive());
    });

    userActivitySessionTrigger$.subscribe(async () => {
      UtilsService.debugLog('User Activity Service: Triggering Session Update');
      const state = rootStore.getState();
      if (
        state.userActivity.isActive &&
        state.networkStatus.networkStatus.isOnline === true &&
        state.checkInState.status === StateStatus.SUCCESS &&
        state.checkInState.isCustomerValid
      ) {
        await checkAndSendOfflinePings(state);
        rootStore.dispatch(
          addSessionPing({
            deviceId: state.deviceDetails.details.id,
            userId: state.userProfile.userProfile.email,
            customerId: state.customerState.customerSettings?.customerId ?? 'unknown',
            domain: state.userProfile.userProfile.domain,
            deviceOrgUnitPath: state.deviceDetails.deviceResource?.deviceOrgUnitPath ?? 'unknown',
            userOrgUnitPath: state.userProfile.userProfile.userOrgUnitPath ?? 'unknown',
            publicIp: state.networkStatus.networkStatus.publicIp,
            privateIp: state.networkStatus.networkStatus.privateIp,
            crosVersion: state.deviceDetails.details.OSVersion ?? 'unknown',
            extensionVersion: extensionApi.getManifest().version,
            timestamp: new Date().toISOString(),
          })
        );
        return;
      }

      // Save the offline session pings
      if (state.userActivity.isActive && state.networkStatus.networkStatus.isOnline === false) {
        rootStore.dispatch(
          addOfflineSessionPing({
            deviceOrgUnitPath: state.deviceDetails?.deviceResource?.deviceOrgUnitPath,
            userOrgUnitPath: state.userProfile?.userProfile?.userOrgUnitPath,
            publicIp: state.networkStatus?.networkStatus?.publicIp,
            privateIp: state.networkStatus?.networkStatus?.privateIp,
            crosVersion: state.deviceDetails?.details?.OSVersion,
            extensionVersion: extensionApi.getManifest().version,
            timestamp: new Date().toISOString(),
          })
        );
      }
    });
  },
};

export default UserActivityService;
